
import { Component, effect, input } from '@angular/core';
import { CompletionStat, HeygenStat, Model, OLlamaChatModelType, TextToSpeechStat, UsageStat } from '@shared/index';
import dayjs from 'dayjs';
import { ApexAxisChartSeries, ApexTooltip, ApexXAxis, NgApexchartsModule } from 'ng-apexcharts';
import { MessageModule } from 'primeng/message';
import { StatisticService } from '../statistic.service';
import { MonthOfYear } from '../statistics-month-selector/statistics-month-selector.component';

@Component({
  selector: 'app-usage-statistic-chart',
  imports: [
    NgApexchartsModule,
    MessageModule
  ],
  templateUrl: './usage-statistic-chart.component.html',
  styleUrl: './usage-statistic-chart.component.scss'
})
export class UsageStatisticChartComponent {
  public readonly model = input.required<Model | OLlamaChatModelType>();
  public readonly usageStats = input.required<UsageStat[]>();
  public readonly selectedMonth = input.required<MonthOfYear>();
  public totalRequests: number = 0;
  public totalValue: number = 0;
  public requestSeries: ApexAxisChartSeries = [];
  public valueSeries: ApexAxisChartSeries = [];
  public xaxis: ApexXAxis = {
    type: 'category',
    categories: [],
    labels: {
      rotate: -45,
      formatter: (val: string) => {
        return val + " " + this.statService.getMonth(this.selectedMonth().month);
      }

    },
    tickAmount: 2,
    tooltip: {
      enabled: false
    }
  };
  public requestTooltip: ApexTooltip = {
    y: {
      formatter: (val: number) => {
        return val + " requests";
      }
    }
  };
  public tokenTooltip: ApexTooltip = {
    y: {
      formatter: (val: number) => {
        return val + " tokens";
      }
    }
  };

  constructor(private statService: StatisticService) {
    effect(() => {
      this.requestSeries = [];
      this.valueSeries = [];
      this.totalRequests = this.usageStats().length;
      this.totalValue = this.usageStats().map((stat) => {
        if (stat.model == 'gpt-4o' ||
          stat.model == 'gpt-4o-mini' ||
          stat.model == 'gpt-4.1' ||
          stat.model == 'gpt-4.1-mini' ||
          stat.model == 'gpt-4.1-nano' ||
          stat.model == 'gpt-5' ||
          stat.model == 'gpt-5-mini' ||
          stat.model == 'gpt-5-nano' ||
          stat.model == 'gpt-5.1' ||
          stat.model == 'gpt-5.2' ||
          stat.model == 'gpt-5.4' ||
          stat.model == 'gpt-5.4-mini' ||
          stat.model == 'gpt-5.4-nano' ||
          stat.model == 'gpt-5.5' ||
          stat.model == 'gpt-5.5-pro' ||
          stat.model == 'gpt-5.6' ||
          stat.model == 'gpt-5.6-sol' ||
          stat.model == 'gpt-5.6-terra' ||
          stat.model == 'gpt-5.6-luna' ||
          stat.model == 'gpt-6-astra' ||
          stat.model == 'deepseek-r1:14b' ||
          stat.model == 'deepseek-r1:8b' ||
          stat.model == 'gemma3:4b' ||
          stat.model == 'llama3.2:3b' ||
          stat.model == 'mistral:7b' ||
          stat.model == 'qwen2.5vl:7b'
        ) {
          return stat.usage.total_tokens;
        }
        if (stat.model === 'heygen_streaming_avatar') {
          return Math.round(stat.duration_ms / 1000);
        } else if (stat.model === 'tts-1' || stat.model == 'gpt-4o-mini-tts') {
          return stat.characters;
        } else {
          return 0;
        }
      }).reduce((a, b) => { return a + b; }, 0);
      this.computeChartData();
    });
  }

  private computeChartData() {
    const gpt4oRequest = {
      name: "GPT-4o",
      data: this.getDataPerDay("gpt-4o", "requests"),
      color: "#ff0000"
    };
    const gpt4ominiRequest = {
      name: "GPT-4o-mini",
      data: this.getDataPerDay("gpt-4o-mini", "requests"),
      color: "#00ff00"
    };
    const gpt41Request = {
      name: "GPT-4.1",
      data: this.getDataPerDay("gpt-4.1", "requests"),
      color: "#9a9c11"
    };
    const gpt41miniRequest = {
      name: "GPT-4.1-mini",
      data: this.getDataPerDay("gpt-4.1-mini", "requests"),
      color: "#752d7c"
    };
    const gpt41nanoRequest = {
      name: "GPT-4.1-nano",
      data: this.getDataPerDay("gpt-4.1-nano", "requests"),
      color: "#495cb8"
    };
    const gpt5Request = {
      name: "GPT-5",
      data: this.getDataPerDay("gpt-5", "requests"),
      color: "#f5a51c"
    };
    const gpt5miniRequest = {
      name: "GPT-5-mini",
      data: this.getDataPerDay("gpt-5-mini", "requests"),
      color: "#dc577f"
    };
    const gpt5nanoRequest = {
      name: "GPT-5-nano",
      data: this.getDataPerDay("gpt-5-nano", "requests"),
      color: "#114563"
    };
    const gpt51Request = {
      name: "GPT-5.1",
      data: this.getDataPerDay("gpt-5.1", "requests"),
      color: "#c48a1c"
    };
    const gpt52Request = {
      name: "GPT-5.2",
      data: this.getDataPerDay("gpt-5.2", "requests"),
      color: "#7c9c1c"
    };
    const gpt54Request = {
      name: "GPT-5.4",
      data: this.getDataPerDay("gpt-5.4", "requests"),
      color: "#1c9c8a"
    };
    const gpt54miniRequest = {
      name: "GPT-5.4-mini",
      data: this.getDataPerDay("gpt-5.4-mini", "requests"),
      color: "#1c6a9c"
    };
    const gpt54nanoRequest = {
      name: "GPT-5.4-nano",
      data: this.getDataPerDay("gpt-5.4-nano", "requests"),
      color: "#4a1c9c"
    };
    const gpt55Request = {
      name: "GPT-5.5",
      data: this.getDataPerDay("gpt-5.5", "requests"),
      color: "#9c1c6a"
    };
    const gpt55proRequest = {
      name: "GPT-5.5-Pro",
      data: this.getDataPerDay("gpt-5.5-pro", "requests"),
      color: "#9c1c1c"
    };
    const gpt56Request = {
      name: "GPT-5.6",
      data: this.getDataPerDay("gpt-5.6", "requests"),
      color: "#3c9c1c"
    };
    const gpt56solRequest = {
      name: "GPT-5.6-Sol",
      data: this.getDataPerDay("gpt-5.6-sol", "requests"),
      color: "#e0a200"
    };
    const gpt56terraRequest = {
      name: "GPT-5.6-Terra",
      data: this.getDataPerDay("gpt-5.6-terra", "requests"),
      color: "#3f7d3f"
    };
    const gpt56lunaRequest = {
      name: "GPT-5.6-Luna",
      data: this.getDataPerDay("gpt-5.6-luna", "requests"),
      color: "#4f5fa8"
    };
    const gpt6astraRequest = {
      name: "GPT-6-Astra",
      data: this.getDataPerDay("gpt-6-astra", "requests"),
      color: "#8a1c9c"
    };
    const deepseekr18bRequest = {
      name: "DEEPSEEK-r1:8b",
      data: this.getDataPerDay("deepseek-r1:8b", "requests"),
      color: "#9ef85e"
    };
    const deepseekr114bRequest = {
      name: "DEEPSEEK-r1:14b",
      data: this.getDataPerDay("deepseek-r1:14b", "requests"),
      color: "#a24a38"
    };
    const gemma34bRequest = {
      name: "GEMMA3:4b",
      data: this.getDataPerDay("gemma3:4b", "requests"),
      color: "#a81288"
    };
    const llama323bRequest = {
      name: "LLAMA3.2:3b",
      data: this.getDataPerDay("llama3.2:3b", "requests"),
      color: "#387b69"
    };
    const mistral7bRequest = {
      name: "MISTRAL:7b",
      data: this.getDataPerDay("mistral:7b", "requests"),
      color: "#c34783"
    };
    const qwen25vl7bRequest = {
      name: "QWEN2.5vl:7b",
      data: this.getDataPerDay("qwen2.5vl:7b", "requests"),
      color: "#55670d"
    };
    const heygenRequest = {
      name: "Heygen",
      data: this.getDataPerDay("heygen_streaming_avatar", "requests"),
      color: "#775DD0"
    };
    const ttsRequest = {
      name: "TTS-1",
      data: this.getDataPerDay("tts-1", "requests"),
      color: "#662E9B"
    };
    const gptttsRequest = {
      name: "gpt-4o-mini-tts",
      data: this.getDataPerDay("gpt-4o-mini-tts", "requests"),
      color: "#25d1e1"
    };


    const gpt4oTokens = {
      name: "GPT-4o",
      data: this.getDataPerDay("gpt-4o", "token"),
      color: "#ff0000"
    };
    const gpt4ominiTokens = {
      name: "GPT-4o-mini",
      data: this.getDataPerDay("gpt-4o-mini", "token"),
      color: "#00ff00"
    };
    const gpt41Tokens = {
      name: "GPT-4.1",
      data: this.getDataPerDay("gpt-4.1", "token"),
      color: "#9a9c11"
    };
    const gpt41miniTokens = {
      name: "GPT-4.1-mini",
      data: this.getDataPerDay("gpt-4.1-mini", "token"),
      color: "#752d7c"
    };
    const gpt41nanoTokens = {
      name: "GPT-4.1-nano",
      data: this.getDataPerDay("gpt-4.1-nano", "token"),
      color: "#495cb8"
    };
    const gpt5Tokens = {
      name: "GPT-5",
      data: this.getDataPerDay("gpt-5", "token"),
      color: "#f5a51c"
    };
    const gpt5miniTokens = {
      name: "GPT-5-mini",
      data: this.getDataPerDay("gpt-5-mini", "token"),
      color: "#dc577f"
    };
    const gpt5nanoTokens = {
      name: "GPT-5-nano",
      data: this.getDataPerDay("gpt-5-nano", "token"),
      color: "#114563"
    };
    const gpt51Tokens = {
      name: "GPT-5.1",
      data: this.getDataPerDay("gpt-5.1", "token"),
      color: "#c48a1c"
    };
    const gpt52Tokens = {
      name: "GPT-5.2",
      data: this.getDataPerDay("gpt-5.2", "token"),
      color: "#7c9c1c"
    };
    const gpt54Tokens = {
      name: "GPT-5.4",
      data: this.getDataPerDay("gpt-5.4", "token"),
      color: "#1c9c8a"
    };
    const gpt54miniTokens = {
      name: "GPT-5.4-mini",
      data: this.getDataPerDay("gpt-5.4-mini", "token"),
      color: "#1c6a9c"
    };
    const gpt54nanoTokens = {
      name: "GPT-5.4-nano",
      data: this.getDataPerDay("gpt-5.4-nano", "token"),
      color: "#4a1c9c"
    };
    const gpt55Tokens = {
      name: "GPT-5.5",
      data: this.getDataPerDay("gpt-5.5", "token"),
      color: "#9c1c6a"
    };
    const gpt55proTokens = {
      name: "GPT-5.5-Pro",
      data: this.getDataPerDay("gpt-5.5-pro", "token"),
      color: "#9c1c1c"
    };
    const gpt56Tokens = {
      name: "GPT-5.6",
      data: this.getDataPerDay("gpt-5.6", "token"),
      color: "#3c9c1c"
    };
    const gpt56solTokens = {
      name: "GPT-5.6-Sol",
      data: this.getDataPerDay("gpt-5.6-sol", "token"),
      color: "#e0a200"
    };
    const gpt56terraTokens = {
      name: "GPT-5.6-Terra",
      data: this.getDataPerDay("gpt-5.6-terra", "token"),
      color: "#3f7d3f"
    };
    const gpt56lunaTokens = {
      name: "GPT-5.6-Luna",
      data: this.getDataPerDay("gpt-5.6-luna", "token"),
      color: "#4f5fa8"
    };
    const gpt6astraTokens = {
      name: "GPT-6-Astra",
      data: this.getDataPerDay("gpt-6-astra", "token"),
      color: "#8a1c9c"
    };
    const deepseekr18bTokens = {
      name: "DEEPSEEK-r1:8b",
      data: this.getDataPerDay("deepseek-r1:8b", "token"),
      color: "#9ef85e"
    };
    const deepseekr114bTokens = {
      name: "DEEPSEEK-r1:14b",
      data: this.getDataPerDay("deepseek-r1:14b", "token"),
      color: "#a24a38"
    };
    const gemma34bTokens = {
      name: "GEMMA3:4b",
      data: this.getDataPerDay("gemma3:4b", "token"),
      color: "#a81288"
    };
    const llama323bTokens = {
      name: "LLAMA3.2:3b",
      data: this.getDataPerDay("llama3.2:3b", "token"),
      color: "#387b69"
    };
    const mistral7bTokens = {
      name: "MISTRAL:7b",
      data: this.getDataPerDay("mistral:7b", "token"),
      color: "#c34783"
    };
    const qwen25vl7bTokens = {
      name: "QWEN2.5vl:7b",
      data: this.getDataPerDay("qwen2.5vl:7b", "token"),
      color: "#55670d"
    };
    const heygenSeconds = {
      name: "Heygen",
      data: this.getDataPerDay("heygen_streaming_avatar", "time"),
      color: "#775DD0"
    };
    const ttsCharacters = {
      name: "TTS-1",
      data: this.getDataPerDay("tts-1", "characters"),
      color: "#662E9B"
    };
    const gptttsCharacters = {
      name: "gpt-4o-mini-tts",
      data: this.getDataPerDay("gpt-4o-mini-tts", "characters"),
      color: "#25d1e1"
    };
    const model = this.model();
    if (model === "gpt-4o") {
      this.requestSeries.push(gpt4oRequest);
      this.valueSeries.push(gpt4oTokens);
    }
    if (model === "gpt-4o-mini") {
      this.requestSeries.push(gpt4ominiRequest);
      this.valueSeries.push(gpt4ominiTokens);
    }

    if (model === "gpt-4.1") {
      this.requestSeries.push(gpt41Request);
      this.valueSeries.push(gpt41Tokens);
    }
    if (model === "gpt-4.1-mini") {
      this.requestSeries.push(gpt41miniRequest);
      this.valueSeries.push(gpt41miniTokens);
    }
    if (model === "gpt-4.1-nano") {
      this.requestSeries.push(gpt41nanoRequest);
      this.valueSeries.push(gpt41nanoTokens);
    }
    if (model === "gpt-5") {
      this.requestSeries.push(gpt5Request);
      this.valueSeries.push(gpt5Tokens);
    }
    if (model === "gpt-5-mini") {
      this.requestSeries.push(gpt5miniRequest);
      this.valueSeries.push(gpt5miniTokens);
    }
    if (model === "gpt-5-nano") {
      this.requestSeries.push(gpt5nanoRequest);
      this.valueSeries.push(gpt5nanoTokens);
    }
    if (model === "gpt-5.1") {
      this.requestSeries.push(gpt51Request);
      this.valueSeries.push(gpt51Tokens);
    }
    if (model === "gpt-5.2") {
      this.requestSeries.push(gpt52Request);
      this.valueSeries.push(gpt52Tokens);
    }
    if (model === "gpt-5.4") {
      this.requestSeries.push(gpt54Request);
      this.valueSeries.push(gpt54Tokens);
    }
    if (model === "gpt-5.4-mini") {
      this.requestSeries.push(gpt54miniRequest);
      this.valueSeries.push(gpt54miniTokens);
    }
    if (model === "gpt-5.4-nano") {
      this.requestSeries.push(gpt54nanoRequest);
      this.valueSeries.push(gpt54nanoTokens);
    }
    if (model === "gpt-5.5") {
      this.requestSeries.push(gpt55Request);
      this.valueSeries.push(gpt55Tokens);
    }
    if (model === "gpt-5.5-pro") {
      this.requestSeries.push(gpt55proRequest);
      this.valueSeries.push(gpt55proTokens);
    }
    if (model === "gpt-5.6") {
      this.requestSeries.push(gpt56Request);
      this.valueSeries.push(gpt56Tokens);
    }
    if (model === "gpt-5.6-sol") {
      this.requestSeries.push(gpt56solRequest);
      this.valueSeries.push(gpt56solTokens);
    }
    if (model === "gpt-5.6-terra") {
      this.requestSeries.push(gpt56terraRequest);
      this.valueSeries.push(gpt56terraTokens);
    }
    if (model === "gpt-5.6-luna") {
      this.requestSeries.push(gpt56lunaRequest);
      this.valueSeries.push(gpt56lunaTokens);
    }
    if (model === "gpt-6-astra") {
      this.requestSeries.push(gpt6astraRequest);
      this.valueSeries.push(gpt6astraTokens);
    }
    if (model === "deepseek-r1:8b") {
      this.requestSeries.push(deepseekr18bRequest);
      this.valueSeries.push(deepseekr18bTokens);
    }
    if (model === "deepseek-r1:14b") {
      this.requestSeries.push(deepseekr114bRequest);
      this.valueSeries.push(deepseekr114bTokens);
    }
    if (model === "gemma3:4b") {
      this.requestSeries.push(gemma34bRequest);
      this.valueSeries.push(gemma34bTokens);
    }
    if (model === "llama3.2:3b") {
      this.requestSeries.push(llama323bRequest);
      this.valueSeries.push(llama323bTokens);
    }
    if (model === "mistral:7b") {
      this.requestSeries.push(mistral7bRequest);
      this.valueSeries.push(mistral7bTokens);
    }
    if (model === "qwen2.5vl:7b") {
      this.requestSeries.push(qwen25vl7bRequest);
      this.valueSeries.push(qwen25vl7bTokens);
    }

    if (model === "heygen_streaming_avatar") {
      this.requestSeries.push(heygenRequest);
      this.valueSeries.push(heygenSeconds);
    }
    if (model === "tts-1") {
      this.requestSeries.push(ttsRequest);
      this.valueSeries.push(ttsCharacters);
    }
    if (model === "gpt-4o-mini-tts") {
      this.requestSeries.push(gptttsRequest);
      this.valueSeries.push(gptttsCharacters);
    }
  }

  private getDataPerDay(model: Model | OLlamaChatModelType, type: "requests" | "token" | "time" | "characters"): number[] {
    const usagePerDay: number[] = [];
    this.xaxis.categories = [];
    for (let i = 0; i < dayjs().set('year', this.selectedMonth().year).set('month', this.selectedMonth().month - 1).daysInMonth(); i++) {
      usagePerDay.push(0);
      (this.xaxis.categories as number[]).push((i + 1));
    }
    this.usageStats().forEach((stat) => {
      if (stat.model !== model) return;
      const dayIndex = dayjs(stat.created).get('date') - 1;
      if (usagePerDay[dayIndex] == undefined) return;
      if (type === "requests") {
        usagePerDay[dayIndex] += 1;
      } else if (type === "token") {
        usagePerDay[dayIndex] += (stat as CompletionStat).usage.total_tokens;
      } else if (type === "time") {
        usagePerDay[dayIndex] += (Math.round((stat as HeygenStat).duration_ms) / 1000);
      } else {
        usagePerDay[dayIndex] += (stat as TextToSpeechStat).characters;
      }
    });
    return usagePerDay;
  }

  protected calculateRequestsToTickAmount() {
    return Math.min(this.totalRequests, 6);
  }
}